import type {
  AdminUserListItem,
  AdminUserProfile,
  AdminUserDetail,
  AdminSessionDetail,
  AdminTimelineEntry,
} from "@/types/admin-user";
import type { AdminConsentState } from "@/types/admin-api";

/**
 * 8 canonical user scenarios. Every mock domain (memberships, hardware,
 * reclaims, fraud, disputes, audit, support) keys off these userIds.
 *
 * Keep this list stable — adding/removing entries breaks every other mock
 * fixture and the contract stability tests.
 */
export const MOCK_USER_IDS = [
  "u_001_alice_active",
  "u_002_bob_trial_pending",
  "u_003_cara_trial_ending",
  "u_004_dave_card_failed",
  "u_005_erin_charged_off",
  "u_006_finn_past_due",
  "u_007_gina_suspended",
  "u_008_hugo_cancelled",
] as const;

export type MockUserId = (typeof MOCK_USER_IDS)[number];

export interface MockUserScenarioMeta {
  userId: MockUserId;
  scenarioLabel: string;
}

export const MOCK_USER_SCENARIOS: MockUserScenarioMeta[] = [
  { userId: "u_001_alice_active", scenarioLabel: "active prepaid user" },
  { userId: "u_002_bob_trial_pending", scenarioLabel: "trial pending delivery" },
  { userId: "u_003_cara_trial_ending", scenarioLabel: "trial ending soon" },
  { userId: "u_004_dave_card_failed", scenarioLabel: "trial card failed" },
  { userId: "u_005_erin_charged_off", scenarioLabel: "trial charged off" },
  { userId: "u_006_finn_past_due", scenarioLabel: "past due active user" },
  { userId: "u_007_gina_suspended", scenarioLabel: "suspended for fraud" },
  { userId: "u_008_hugo_cancelled", scenarioLabel: "cancelled in-term" },
];

export const USER_LIST_ITEMS: Record<MockUserId, AdminUserListItem> = {
  u_001_alice_active: {
    userId: "u_001_alice_active",
    email: "alice@example.com",
    displayName: "Alice Andersson",
    avatarUrl: null,
    membershipState: "active",
    path: "direct",
    futureBilling: "annual",
    joinedAt: "2024-08-12T10:14:00Z",
    primarySerial: "ZB1-A001-77F2",
    authProviders: ["password"],
    lastActiveAt: "2026-05-19T20:11:00Z",
    riskFlags: [],
    countryCode: "SE",
  },
  u_002_bob_trial_pending: {
    userId: "u_002_bob_trial_pending",
    email: "bob@example.com",
    displayName: "Bob Bergman",
    avatarUrl: null,
    membershipState: "pending_delivery",
    path: "trial",
    futureBilling: "annual",
    joinedAt: "2026-05-14T08:02:00Z",
    primarySerial: "ZB1-B002-A41C",
    authProviders: ["password", "google"],
    lastActiveAt: "2026-05-18T17:42:00Z",
    riskFlags: [],
    countryCode: "SE",
  },
  u_003_cara_trial_ending: {
    userId: "u_003_cara_trial_ending",
    email: "cara@example.com",
    displayName: "Cara Carlsson",
    avatarUrl: null,
    membershipState: "trial_ending_soon",
    path: "paid_trial",
    futureBilling: "monthly",
    joinedAt: "2026-04-28T14:50:00Z",
    primarySerial: "ZB1-C003-19B0",
    authProviders: ["apple"],
    lastActiveAt: "2026-05-20T07:33:00Z",
    riskFlags: [],
    countryCode: "NO",
  },
  u_004_dave_card_failed: {
    userId: "u_004_dave_card_failed",
    email: "dave@example.com",
    displayName: "Dave Dahlberg",
    avatarUrl: null,
    membershipState: "trial_card_failed",
    path: "trial",
    futureBilling: "annual",
    joinedAt: "2026-04-22T11:15:00Z",
    primarySerial: "ZB1-D004-66E1",
    authProviders: ["password"],
    lastActiveAt: "2026-05-09T22:01:00Z",
    riskFlags: ["card_velocity"],
    countryCode: "DK",
  },
  u_005_erin_charged_off: {
    userId: "u_005_erin_charged_off",
    email: "erin@example.com",
    displayName: "Erin Ek",
    avatarUrl: null,
    membershipState: "trial_charged_off",
    path: "trial",
    futureBilling: "none",
    joinedAt: "2026-02-15T09:30:00Z",
    primarySerial: "ZB1-E005-23A5",
    authProviders: ["password"],
    lastActiveAt: "2026-04-02T13:20:00Z",
    riskFlags: ["trial_abuse_history"],
    countryCode: "FI",
  },
  u_006_finn_past_due: {
    userId: "u_006_finn_past_due",
    email: "finn@example.com",
    displayName: "Finn Falk",
    avatarUrl: null,
    membershipState: "past_due",
    path: "direct",
    futureBilling: "monthly",
    joinedAt: "2025-09-05T16:01:00Z",
    primarySerial: "ZB1-F006-90DD",
    authProviders: ["password", "google"],
    lastActiveAt: "2026-05-15T19:00:00Z",
    riskFlags: ["card_velocity"],
    countryCode: "DE",
  },
  u_007_gina_suspended: {
    userId: "u_007_gina_suspended",
    email: "gina@example.com",
    displayName: "Gina Grön",
    avatarUrl: null,
    membershipState: "suspended",
    path: "direct",
    futureBilling: "none",
    joinedAt: "2025-11-19T12:00:00Z",
    primarySerial: "ZB1-G007-44CB",
    authProviders: ["password"],
    lastActiveAt: "2026-03-14T10:05:00Z",
    riskFlags: ["household_repeat", "stripe_radar_high"],
    countryCode: "NL",
  },
  u_008_hugo_cancelled: {
    userId: "u_008_hugo_cancelled",
    email: "hugo@example.com",
    displayName: "Hugo Holm",
    avatarUrl: null,
    membershipState: "cancelled",
    path: "direct",
    futureBilling: "none",
    joinedAt: "2024-12-01T09:00:00Z",
    primarySerial: "ZB1-H008-E2F8",
    authProviders: ["password"],
    lastActiveAt: "2026-05-10T18:30:00Z",
    riskFlags: [],
    countryCode: "SE",
  },
};

export const USER_PROFILES: Record<MockUserId, AdminUserProfile> = {
  u_001_alice_active: {
    userId: "u_001_alice_active",
    email: "alice@example.com",
    displayName: "Alice Andersson",
    avatarUrl: null,
    countryCode: "SE",
    locale: "sv-SE",
    phoneE164: "+46701112233",
    authProviders: ["password"],
    accountStatus: "active",
    joinedAt: "2024-08-12T10:14:00Z",
    lastActiveAt: "2026-05-19T20:11:00Z",
    ltvMinor: 84_000,
    deviceCount: 1,
  },
  u_002_bob_trial_pending: {
    userId: "u_002_bob_trial_pending",
    email: "bob@example.com",
    displayName: "Bob Bergman",
    avatarUrl: null,
    countryCode: "SE",
    locale: "sv-SE",
    phoneE164: "+46707778899",
    authProviders: ["password", "google"],
    accountStatus: "active",
    joinedAt: "2026-05-14T08:02:00Z",
    lastActiveAt: "2026-05-18T17:42:00Z",
    ltvMinor: 0,
    deviceCount: 1,
  },
  u_003_cara_trial_ending: {
    userId: "u_003_cara_trial_ending",
    email: "cara@example.com",
    displayName: "Cara Carlsson",
    avatarUrl: null,
    countryCode: "NO",
    locale: "nb-NO",
    phoneE164: "+4790112233",
    authProviders: ["apple"],
    accountStatus: "active",
    joinedAt: "2026-04-28T14:50:00Z",
    lastActiveAt: "2026-05-20T07:33:00Z",
    ltvMinor: 9_900,
    deviceCount: 1,
  },
  u_004_dave_card_failed: {
    userId: "u_004_dave_card_failed",
    email: "dave@example.com",
    displayName: "Dave Dahlberg",
    avatarUrl: null,
    countryCode: "DK",
    locale: "da-DK",
    phoneE164: "+4520304050",
    authProviders: ["password"],
    accountStatus: "active",
    joinedAt: "2026-04-22T11:15:00Z",
    lastActiveAt: "2026-05-09T22:01:00Z",
    ltvMinor: 0,
    deviceCount: 1,
  },
  u_005_erin_charged_off: {
    userId: "u_005_erin_charged_off",
    email: "erin@example.com",
    displayName: "Erin Ek",
    avatarUrl: null,
    countryCode: "FI",
    locale: "fi-FI",
    phoneE164: "+358401234567",
    authProviders: ["password"],
    accountStatus: "locked",
    joinedAt: "2026-02-15T09:30:00Z",
    lastActiveAt: "2026-04-02T13:20:00Z",
    ltvMinor: 0,
    deviceCount: 0,
  },
  u_006_finn_past_due: {
    userId: "u_006_finn_past_due",
    email: "finn@example.com",
    displayName: "Finn Falk",
    avatarUrl: null,
    countryCode: "DE",
    locale: "de-DE",
    phoneE164: "+4915112345678",
    authProviders: ["password", "google"],
    accountStatus: "active",
    joinedAt: "2025-09-05T16:01:00Z",
    lastActiveAt: "2026-05-15T19:00:00Z",
    ltvMinor: 27_900,
    deviceCount: 1,
  },
  u_007_gina_suspended: {
    userId: "u_007_gina_suspended",
    email: "gina@example.com",
    displayName: "Gina Grön",
    avatarUrl: null,
    countryCode: "NL",
    locale: "nl-NL",
    phoneE164: "+31612345678",
    authProviders: ["password"],
    accountStatus: "locked",
    joinedAt: "2025-11-19T12:00:00Z",
    lastActiveAt: "2026-03-14T10:05:00Z",
    ltvMinor: 4_900,
    deviceCount: 0,
  },
  u_008_hugo_cancelled: {
    userId: "u_008_hugo_cancelled",
    email: "hugo@example.com",
    displayName: "Hugo Holm",
    avatarUrl: null,
    countryCode: "SE",
    locale: "sv-SE",
    phoneE164: "+46703334455",
    authProviders: ["password"],
    accountStatus: "active",
    joinedAt: "2024-12-01T09:00:00Z",
    lastActiveAt: "2026-05-10T18:30:00Z",
    ltvMinor: 84_000,
    deviceCount: 1,
  },
};

/**
 * Post-conversion defaults per Auth Brief lines 410-411: product_improvement
 * and research default true once the user converts, false during trial.
 * research_recontact defaults false; marketing defaults false.
 */
const baseConsent: AdminConsentState = {
  personalization: true,
  productImprovement: true,
  research: true,
  researchRecontact: false,
  marketing: false,
  negativeOptionDisclosure: null,
  versionsAccepted: {
    tos: "2026-02-01",
    privacy: "2026-02-01",
    commitment: "1.0",
    dataUse: "3",
    negativeOption: null,
  },
  // WF-72: mock users have no ledger-recorded acceptance dates.
  versionsAcceptedAt: {},
};

/** Trial users: the value-exchange is incomplete, so the two data-use consents
 *  are off until conversion, and the negative-option disclosure is on (it must
 *  be true to enter the trial state at all). */
const trialConsent: AdminConsentState = {
  ...baseConsent,
  productImprovement: false,
  research: false,
  negativeOptionDisclosure: true,
  versionsAccepted: { ...baseConsent.versionsAccepted, negativeOption: "3" },
};

export const USER_CONSENT: Record<MockUserId, AdminConsentState> = {
  u_001_alice_active: { ...baseConsent, marketing: true },
  u_002_bob_trial_pending: { ...trialConsent },
  u_003_cara_trial_ending: { ...trialConsent },
  u_004_dave_card_failed: { ...trialConsent },
  u_005_erin_charged_off: { ...trialConsent },
  u_006_finn_past_due: { ...baseConsent, marketing: true },
  u_007_gina_suspended: { ...baseConsent },
  // Never asked — exercises the "not recorded" rendering path.
  u_008_hugo_cancelled: {
    ...baseConsent,
    productImprovement: null,
    research: null,
    researchRecontact: null,
    marketing: null,
    versionsAccepted: {
      tos: null,
      privacy: null,
      commitment: null,
      dataUse: null,
      negativeOption: null,
    },
  },
};

export const USER_DETAILS: Record<MockUserId, AdminUserDetail> = {
  u_001_alice_active: {
    profile: USER_PROFILES.u_001_alice_active,
    consent: USER_CONSENT.u_001_alice_active,
    primarySerial: "ZB1-A001-77F2",
    riskLevel: "low",
    riskFlags: [],
    hasOpenDispute: false,
    hasOpenReclaim: false,
    path: "direct",
    membershipState: "active",
  },
  u_002_bob_trial_pending: {
    profile: USER_PROFILES.u_002_bob_trial_pending,
    consent: USER_CONSENT.u_002_bob_trial_pending,
    primarySerial: "ZB1-B002-A41C",
    riskLevel: "low",
    riskFlags: [],
    hasOpenDispute: false,
    hasOpenReclaim: false,
    path: "trial",
    membershipState: "pending_delivery",
  },
  u_003_cara_trial_ending: {
    profile: USER_PROFILES.u_003_cara_trial_ending,
    consent: USER_CONSENT.u_003_cara_trial_ending,
    primarySerial: "ZB1-C003-19B0",
    riskLevel: "low",
    riskFlags: [],
    hasOpenDispute: false,
    hasOpenReclaim: false,
    path: "paid_trial",
    membershipState: "trial_ending_soon",
  },
  u_004_dave_card_failed: {
    profile: USER_PROFILES.u_004_dave_card_failed,
    consent: USER_CONSENT.u_004_dave_card_failed,
    primarySerial: "ZB1-D004-66E1",
    riskLevel: "medium",
    riskFlags: ["card_velocity"],
    hasOpenDispute: false,
    hasOpenReclaim: false,
    path: "trial",
    membershipState: "trial_card_failed",
  },
  u_005_erin_charged_off: {
    profile: USER_PROFILES.u_005_erin_charged_off,
    consent: USER_CONSENT.u_005_erin_charged_off,
    primarySerial: "ZB1-E005-23A5",
    riskLevel: "high",
    riskFlags: ["trial_abuse_history"],
    hasOpenDispute: false,
    hasOpenReclaim: true,
    path: "trial",
    membershipState: "trial_charged_off",
  },
  u_006_finn_past_due: {
    profile: USER_PROFILES.u_006_finn_past_due,
    consent: USER_CONSENT.u_006_finn_past_due,
    primarySerial: "ZB1-F006-90DD",
    riskLevel: "medium",
    riskFlags: ["card_velocity"],
    hasOpenDispute: true,
    hasOpenReclaim: false,
    path: "direct",
    membershipState: "past_due",
  },
  u_007_gina_suspended: {
    profile: USER_PROFILES.u_007_gina_suspended,
    consent: USER_CONSENT.u_007_gina_suspended,
    primarySerial: "ZB1-G007-44CB",
    riskLevel: "critical",
    riskFlags: ["household_repeat", "stripe_radar_high"],
    hasOpenDispute: false,
    hasOpenReclaim: true,
    path: "direct",
    membershipState: "suspended",
  },
  u_008_hugo_cancelled: {
    profile: USER_PROFILES.u_008_hugo_cancelled,
    consent: USER_CONSENT.u_008_hugo_cancelled,
    primarySerial: "ZB1-H008-E2F8",
    riskLevel: "low",
    riskFlags: [],
    hasOpenDispute: false,
    hasOpenReclaim: false,
    path: "direct",
    membershipState: "cancelled",
  },
};

const session = (
  sessionId: string,
  lastSeenAt: string,
  current: boolean,
  device = "Chrome · macOS",
): AdminSessionDetail => ({
  sessionId,
  createdAt: lastSeenAt,
  lastSeenAt,
  ipAddress: "203.0.113.10",
  userAgent: "Mozilla/5.0 mock",
  deviceLabel: device,
  current,
  revokedAt: null,
});

export const USER_SESSIONS: Record<MockUserId, AdminSessionDetail[]> = {
  u_001_alice_active: [
    session("s_001_a", "2026-05-19T20:11:00Z", true),
    session("s_001_b", "2026-05-10T08:22:00Z", false, "Safari · iOS"),
  ],
  u_002_bob_trial_pending: [
    session("s_002_a", "2026-05-18T17:42:00Z", true, "Safari · iOS"),
  ],
  u_003_cara_trial_ending: [
    session("s_003_a", "2026-05-20T07:33:00Z", true, "Safari · iOS"),
  ],
  u_004_dave_card_failed: [
    session("s_004_a", "2026-05-09T22:01:00Z", false),
  ],
  u_005_erin_charged_off: [
    session("s_005_a", "2026-04-02T13:20:00Z", false),
  ],
  u_006_finn_past_due: [
    session("s_006_a", "2026-05-15T19:00:00Z", true),
  ],
  u_007_gina_suspended: [
    session("s_007_a", "2026-03-14T10:05:00Z", false),
  ],
  u_008_hugo_cancelled: [
    session("s_008_a", "2026-05-10T18:30:00Z", true),
  ],
};

const timeline = (
  entryId: string,
  kind: AdminTimelineEntry["kind"],
  occurredAt: string,
  summary: string,
  extra: Partial<Omit<AdminTimelineEntry, "entryId" | "kind" | "occurredAt" | "summary">> = {},
): AdminTimelineEntry => ({
  entryId,
  kind,
  occurredAt,
  summary,
  actor: { type: "system", id: "system", displayName: "Zone System" },
  ...extra,
});

export const USER_TIMELINE: Record<MockUserId, AdminTimelineEntry[]> = {
  u_001_alice_active: [
    timeline(
      "t_001_a",
      "membership",
      "2025-11-04T09:14:00Z",
      "Trial signup",
      {
        title: "Trial signup",
        body: "wear.zone checkout, trial path, future-billing: annual. Card vaulted via setup_intent.",
        tone: "ok",
        icon: "plus",
        meta: [
          { label: "AVS", value: "pass" },
          { label: "Radar score", value: "8" },
        ],
      },
    ),
    timeline(
      "t_001_b",
      "hardware",
      "2025-11-04T09:14:00Z",
      "Hardware shipped",
      {
        title: "Hardware shipped",
        body: "Bound serials ZB1-A001-77F2-L / ZB1-A001-77F2-R, refurbished: false.",
        tone: "ok",
        icon: "pkg",
      },
    ),
    timeline(
      "t_001_c",
      "hardware",
      "2025-11-07T14:22:00Z",
      "Hardware delivered",
      {
        title: "Hardware delivered",
        body: "Carrier confirmation. Trial clock starts. Welcome push sent.",
        tone: "ok",
        icon: "check",
      },
    ),
    timeline(
      "t_001_d",
      "membership",
      "2025-11-21T09:00:00Z",
      "Trial midpoint reminder",
      {
        title: "Trial midpoint reminder",
        body: "Single email sent. No urgency framing.",
        tone: "info",
        icon: "mail",
      },
    ),
    timeline(
      "t_001_e",
      "support",
      "2026-02-18T11:05:00Z",
      "Note added by Kai Ramirez",
      {
        title: "Note added by Kai Ramirez",
        body: "Confirmed shipping address change OK via support ticket #2814.",
        tone: "info",
        icon: "note",
        timeFormat: "date",
        actor: { type: "admin", id: "adm_kai", displayName: "Kai Ramirez" },
      },
    ),
    timeline(
      "t_001_f",
      "fraud",
      "2026-03-02T14:22:00Z",
      "Suspicious sign-in alert",
      {
        title: "Suspicious sign-in alert",
        body: "New location: San Francisco, US · device: Pixel 8. User confirmed via email — no action taken.",
        tone: "warn",
        icon: "alert",
        timeFormat: "date",
      },
    ),
    timeline(
      "t_001_g",
      "auth",
      "2026-04-18T09:00:00Z",
      "Password reset (user-initiated)",
      {
        title: "Password reset (user-initiated)",
        tone: "info",
        icon: "lock",
        timeFormat: "date",
        actor: { type: "user", id: "u_001_alice_active", displayName: "Alice Andersson" },
      },
    ),
    timeline(
      "t_001_h",
      "membership",
      "2025-11-28T09:00:00Z",
      "Trial ending soon banner enabled",
      {
        title: "Trial ending soon banner enabled",
        body: "App in-app banner active. Deep-link to my.wear.zone/account.",
        tone: "warn",
        icon: "alert",
      },
    ),
    timeline(
      "t_001_i",
      "membership",
      "2025-12-04T09:14:00Z",
      "Trial converted",
      {
        title: "Trial converted",
        body: "$348 charged on vaulted method. State → active. 12-month commitment begins.",
        tone: "ok",
        icon: "check",
        meta: [
          { label: "Stripe", value: "ch_3PQz4xLk...", mono: true },
          { label: "Amount", value: "$348.00" },
        ],
      },
    ),
    timeline(
      "t_001_j",
      "membership",
      "2024-08-12T10:20:00Z",
      "Activated annual membership",
      {
        title: "Activated annual membership",
        body: "Direct annual path — prepaid signup completed.",
        tone: "ok",
        icon: "check",
      },
    ),
  ],
  u_002_bob_trial_pending: [
    timeline("t_002_a", "auth", "2026-05-14T08:02:00Z", "Account created"),
    timeline(
      "t_002_b",
      "membership",
      "2026-05-14T08:03:00Z",
      "Trial started · pending shipment",
    ),
  ],
  u_003_cara_trial_ending: [
    timeline("t_003_a", "auth", "2026-04-28T14:50:00Z", "Account created"),
    timeline(
      "t_003_b",
      "membership",
      "2026-04-28T14:51:00Z",
      "Paid trial started",
    ),
    timeline(
      "t_003_c",
      "membership",
      "2026-05-19T09:00:00Z",
      "Trial-ending banner shown (D21)",
    ),
  ],
  u_004_dave_card_failed: [
    timeline("t_004_a", "auth", "2026-04-22T11:15:00Z", "Account created"),
    timeline(
      "t_004_b",
      "payment",
      "2026-05-13T11:15:00Z",
      "Card authorization failed (insufficient_funds)",
    ),
  ],
  u_005_erin_charged_off: [
    timeline("t_005_a", "auth", "2026-02-15T09:30:00Z", "Account created"),
    timeline(
      "t_005_b",
      "membership",
      "2026-03-15T09:30:00Z",
      "Trial charge-off — headband not returned by deadline",
    ),
    timeline(
      "t_005_c",
      "reclaim",
      "2026-03-20T10:00:00Z",
      "Reclaim record marked charged_off",
    ),
  ],
  u_006_finn_past_due: [
    timeline("t_006_a", "auth", "2025-09-05T16:01:00Z", "Account created"),
    timeline(
      "t_006_b",
      "payment",
      "2026-05-01T08:00:00Z",
      "Monthly renewal failed — past_due",
    ),
    timeline(
      "t_006_c",
      "dispute",
      "2026-05-12T11:30:00Z",
      "Cardholder disputed renewal charge",
    ),
  ],
  u_007_gina_suspended: [
    timeline("t_007_a", "auth", "2025-11-19T12:00:00Z", "Account created"),
    timeline(
      "t_007_b",
      "fraud",
      "2026-03-12T09:00:00Z",
      "Household_repeat signal triggered",
    ),
    timeline(
      "t_007_c",
      "membership",
      "2026-03-14T10:00:00Z",
      "Account suspended by support",
    ),
  ],
  u_008_hugo_cancelled: [
    timeline("t_008_a", "auth", "2024-12-01T09:00:00Z", "Account created"),
    timeline(
      "t_008_b",
      "membership",
      "2024-12-01T09:05:00Z",
      "Activated annual membership",
    ),
    timeline(
      "t_008_c",
      "membership",
      "2026-05-10T18:30:00Z",
      "Membership cancelled — runs until 2026-12-01",
    ),
    timeline(
      "t_008_d",
      "consent",
      "2026-05-10T18:35:00Z",
      "Requested account deletion (deferred until term end)",
    ),
  ],
};
